import { memo, useCallback, useEffect, useRef, useState } from "react";
import { TerminalWindow } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";

/**
 * Finds the nearest ancestor with `data-message-id` and returns the element + ID.
 */
function findMessageElement(node: Node): { el: Element; id: string } | null {
  let el: Element | null =
    node.nodeType === Node.ELEMENT_NODE
      ? (node as Element)
      : node.parentElement;
  while (el) {
    const id = el.getAttribute("data-message-id");
    if (id) return { el, id };
    el = el.parentElement;
  }
  return null;
}

/** Track the element we tagged so we can clean up. */
let taggedElement: Element | null = null;

function tagMessage(el: Element | null) {
  if (taggedElement && taggedElement !== el) {
    taggedElement.removeAttribute("data-text-selected");
  }
  if (el) {
    el.setAttribute("data-text-selected", "");
  }
  taggedElement = el;
}

// Inject selection styles once into <head>
const STYLE_ID = "ano-selection-toolbar-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    [data-message-id] ::selection {
      background: hsl(var(--primary));
      color: hsl(var(--background));
    }
    [data-text-selected] .absolute[class*="-top-"] {
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
}

interface SelectionToolbarProps {
  /** Ref to the scrollable container that holds all messages. */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Called with the message ID and selected text. */
  onSendToShell: (messageId: string, selectedText: string) => void;
}

/**
 * Floating toolbar that appears when the user selects text inside a message.
 * Mounted once at the message-list level; detects which message the selection
 * is in via `data-message-id`.
 *
 * Keyboard shortcut: Enter (when toolbar visible) or Cmd+Shift+L.
 */
export const SelectionToolbar = memo(function SelectionToolbar({
  containerRef,
  onSendToShell,
}: SelectionToolbarProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [messageId, setMessageId] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState("");
  const [pointerPos, setPointerPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [isDragging, setIsDragging] = useState(false);
  const toolbarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let rafId = 0;

    const clear = () => {
      setRect(null);
      setMessageId(null);
      setSelectedText("");
      setPointerPos(null);
      tagMessage(null);
    };

    const update = () => {
      rafId = 0;
      const sel = window.getSelection();
      const container = containerRef.current;
      if (!sel || !container || sel.isCollapsed || sel.rangeCount === 0) {
        clear();
        return;
      }

      const range = sel.getRangeAt(0);
      if (!container.contains(range.commonAncestorContainer)) {
        clear();
        return;
      }

      const match = findMessageElement(range.startContainer);
      if (!match) {
        clear();
        return;
      }

      const text = sel.toString().trim();
      if (!text) {
        clear();
        return;
      }

      setMessageId(match.id);
      setSelectedText(text);
      setRect(range.getBoundingClientRect());
      tagMessage(match.el);
    };

    // Coalesce selectionchange bursts (fires per keystroke in any
    // contenteditable) into one update per frame. Avoids per-keystroke
    // DOM walks and layout-forcing getBoundingClientRect calls.
    const schedule = () => {
      if (rafId !== 0) return;
      rafId = requestAnimationFrame(update);
    };

    document.addEventListener("selectionchange", schedule);
    return () => {
      document.removeEventListener("selectionchange", schedule);
      if (rafId !== 0) cancelAnimationFrame(rafId);
      tagMessage(null);
    };
  }, [containerRef]);

  // Track mouse drag so we can position the toolbar at the pointer on release
  // and avoid flickering the toolbar mid-drag.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      setIsDragging(true);
      setPointerPos(null);
    };
    const onMouseUp = (e: MouseEvent) => {
      if (e.button !== 0) return;
      setIsDragging(false);
      setPointerPos({ x: e.clientX, y: e.clientY });
    };
    container.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      container.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [containerRef]);

  const dismiss = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setRect(null);
    setMessageId(null);
    setSelectedText("");
    tagMessage(null);
  }, []);

  // Keep latest values in refs so the keydown listener binds once and doesn't
  // re-register on every parent re-render (onSendToShell identity churns when
  // MessageArea rebuilds messageById / renderUserMap).
  const messageIdRef = useRef(messageId);
  const selectedTextRef = useRef(selectedText);
  const onSendToShellRef = useRef(onSendToShell);
  const dismissRef = useRef(dismiss);
  useEffect(() => {
    messageIdRef.current = messageId;
    selectedTextRef.current = selectedText;
    onSendToShellRef.current = onSendToShell;
    dismissRef.current = dismiss;
  });

  // Keyboard shortcuts: Enter (send) or Cmd+Shift+L
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const id = messageIdRef.current;
      const text = selectedTextRef.current;
      if (!id || !text) return;

      // Enter sends the selection to the shell (but not if typing in an input)
      if (e.key === "Enter" && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        const editable = (e.target as HTMLElement)?.isContentEditable;
        if (tag === "INPUT" || tag === "TEXTAREA" || editable) return;
        e.preventDefault();
        onSendToShellRef.current(id, text);
        dismissRef.current();
        return;
      }

      // Cmd+Shift+L also works
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "l"
      ) {
        e.preventDefault();
        onSendToShellRef.current(id, text);
        dismissRef.current();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleClick = useCallback(() => {
    if (!messageId) return;
    onSendToShell(messageId, selectedText);
    dismiss();
  }, [messageId, selectedText, onSendToShell, dismiss]);

  if (!rect || !messageId || !selectedText || isDragging) return null;

  const TOOLBAR_WIDTH = 160;
  const TOOLBAR_HEIGHT = 32;
  const OFFSET = 12;

  let top: number;
  let left: number;
  if (pointerPos) {
    const wouldOverflowBottom =
      pointerPos.y + OFFSET + TOOLBAR_HEIGHT > window.innerHeight - 4;
    top = wouldOverflowBottom
      ? Math.max(4, pointerPos.y - OFFSET - TOOLBAR_HEIGHT)
      : pointerPos.y + OFFSET;
    left = Math.max(
      4,
      Math.min(
        pointerPos.x - TOOLBAR_WIDTH / 2,
        window.innerWidth - TOOLBAR_WIDTH - 4,
      ),
    );
  } else {
    // Keyboard-driven selection: fall back to rect-based placement above.
    top = Math.max(4, rect.top - 36);
    left = Math.max(
      4,
      Math.min(
        rect.left + rect.width / 2 - TOOLBAR_WIDTH / 2,
        window.innerWidth - TOOLBAR_WIDTH - 4,
      ),
    );
  }

  return (
    <>
      <Button
        ref={toolbarRef}
        size="sm"
        onMouseDown={(e) => {
          e.preventDefault();
          handleClick();
        }}
        className="fixed z-50 rounded-full shadow-md"
        style={
          {
            top,
            left,
            "--btn-default-bg":
              "linear-gradient(hsl(var(--primary) / 0.15), hsl(var(--primary) / 0.15)), hsl(var(--background))",
            "--btn-default-hover":
              "linear-gradient(hsl(var(--primary) / 0.25), hsl(var(--primary) / 0.25)), hsl(var(--background))",
          } as React.CSSProperties
        }
        title="Send selection to shell (Enter)"
      >
        <TerminalWindow size={13} weight="Bold" />
        Send to Shell (Enter)
      </Button>
    </>
  );
});
